import { useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  IconButton,
  Link,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api, ApiError } from '@/api/client';
import { OPTIONS_KEY } from './assistant.api';

const CONSOLE_KEYS_URL = 'https://console.anthropic.com/settings/keys';
export const CLAUDE_CONNECTORS_URL = 'https://claude.ai/settings/connectors';

/**
 * The one-time step before someone's first question.
 *
 * The main way is their Claude subscription: Diwan is added to Claude as a
 * connector, and the questions are asked in Claude, on their own plan. A
 * website cannot run a Claude subscription inside its own chat, so the chat
 * itself happens in Claude.
 *
 * The other way, for anyone with API credit, is an API key — then the chat
 * runs here, in the popup.
 */
export function AssistantConnect({
  connectorUrl,
  onLinked,
}: {
  connectorUrl: string | null;
  onLinked: () => void;
}) {
  const { t } = useTranslation();

  return (
    <Stack spacing={2} sx={{ p: 2.5, overflowY: 'auto' }}>
      {connectorUrl ? (
        <ClaudeSetup connectorUrl={connectorUrl} onLinked={onLinked} />
      ) : (
        <Typography variant="subtitle1" fontWeight={700}>
          {t('Connect your Claude account')}
        </Typography>
      )}

      <Accordion disableGutters variant="outlined" defaultExpanded={!connectorUrl} sx={{ '&:before': { display: 'none' } }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <Box>
            <Typography variant="subtitle2">{t('Or chat here with an API key')}</Typography>
            <Typography variant="caption" color="text.secondary">
              {t('Billed on Anthropic API credit, not on a Claude subscription.')}
            </Typography>
          </Box>
        </AccordionSummary>
        <AccordionDetails>
          <ApiKeyForm />
        </AccordionDetails>
      </Accordion>
    </Stack>
  );
}

function ClaudeSetup({ connectorUrl, onLinked }: { connectorUrl: string; onLinked: () => void }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);
  // Claude's servers call the connector, so an address only this computer
  // can reach will not work until Diwan is online.
  const unreachable = /^https?:\/\/(localhost|127\.0\.0\.1|0\.0\.0\.0)(:|\/)/.test(connectorUrl);

  return (
    <>
      <Stack spacing={0.5}>
        <Typography variant="subtitle1" fontWeight={700}>
          {t('Use your Claude subscription')}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {t('Add Diwan to Claude once. Then ask about your data in Claude, on your own plan — no API key or credit needed.')}
        </Typography>
      </Stack>

      <Box component="ol" sx={{ m: 0, ps: 2.5, '& li': { mb: 1 }, typography: 'body2' }}>
        <li>
          {t('Copy this address:')}
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ mt: 0.5, bgcolor: 'action.hover', borderRadius: 1, px: 1.5, py: 0.5 }}
          >
            <Typography variant="body2" sx={{ flex: 1, fontFamily: 'monospace', direction: 'ltr', overflowWrap: 'anywhere' }}>
              {connectorUrl}
            </Typography>
            <Tooltip title={copied ? t('Copied') : t('common.copy')}>
              <IconButton
                size="small"
                onClick={async () => {
                  await navigator.clipboard.writeText(connectorUrl);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </li>
        <li>
          {t('In Claude, open Settings → Connectors and choose “Add custom connector”. Name it Diwan and paste the address.')}{' '}
          <Link
            href={CLAUDE_CONNECTORS_URL}
            target="_blank"
            rel="noopener noreferrer"
            sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
          >
            {t('Open Claude')} <OpenInNewIcon sx={{ fontSize: 14 }} />
          </Link>
        </li>
        <li>{t('Choose Connect and sign in with your Diwan account.')}</li>
      </Box>

      {unreachable && (
        <Alert severity="warning" sx={{ py: 0 }}>
          {t('This address only works on this computer. Claude can reach it once Diwan is online.')}
        </Alert>
      )}

      <Typography variant="caption" color="text.secondary">
        {t('On a Team or Enterprise plan, an owner of your Claude organisation adds the connector first.')}
      </Typography>

      <Button variant="contained" onClick={onLinked}>
        {t('Done — take me to Claude')}
      </Button>
    </>
  );
}

function ApiKeyForm() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [key, setKey] = useState('');

  const connect = useMutation({
    // Its error is rendered beside the action, not as a toast.
    meta: { inlineError: true },
    mutationFn: () => api.put('/assistant/credential', { apiKey: key.trim() }),
    onSuccess: async () => {
      setKey('');
      await queryClient.invalidateQueries({ queryKey: OPTIONS_KEY });
    },
  });

  const errorText = (() => {
    const error = connect.error;
    if (!error) return null;
    if (error instanceof ApiError && error.code === 'assistant.key_invalid') {
      return t('Anthropic did not accept that key. Check it and try again.');
    }
    if (error instanceof ApiError && error.code === 'assistant.key_unverified') {
      return t('Could not reach Anthropic to check the key. Try again in a moment.');
    }
    return t('Could not connect. Try again.');
  })();

  return (
    <Stack spacing={1.5}>
      <Typography variant="body2" color="text.secondary">
        {t('Create a key in the Anthropic console and paste it here.')}{' '}
        <Link href={CONSOLE_KEYS_URL} target="_blank" rel="noopener noreferrer" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
          {t('Open console')} <OpenInNewIcon sx={{ fontSize: 14 }} />
        </Link>
      </Typography>
      <Alert severity="info" sx={{ py: 0 }}>
        {t('The key needs credit on the Anthropic account (Plans & Billing). A Claude Pro or Max subscription does not cover it.')}
      </Alert>
      <form
        onSubmit={(event) => {
          event.preventDefault();
          if (key.trim()) connect.mutate();
        }}
      >
        <Stack spacing={1.5}>
          <TextField
            label={t('API key')}
            placeholder="sk-ant-…"
            value={key}
            onChange={(event) => setKey(event.target.value)}
            type="password"
            autoComplete="off"
            size="small"
            fullWidth
            inputProps={{ dir: 'ltr', spellCheck: false }}
          />
          {errorText && <Alert severity="error">{errorText}</Alert>}
          <Button type="submit" variant="outlined" disabled={!key.trim() || connect.isPending}>
            {connect.isPending ? t('Checking…') : t('Connect')}
          </Button>
          <Typography variant="caption" color="text.secondary">
            {t('The key is checked with Anthropic, stored encrypted, and never shown again. You can disconnect at any time.')}
          </Typography>
        </Stack>
      </form>
    </Stack>
  );
}
